import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { FileText, Trash2, X, Package, Truck, ChevronDown, ChevronRight, Download, Send, Mail, ThumbsUp, ThumbsDown, RotateCcw, ShoppingCart, CheckCircle2 } from "lucide-react";
import { generateQuotePDF, buildQuoteShareText } from "@/lib/quotePdf";
import SupplierOrderDialog from "@/components/SupplierOrderDialog";

/**
 * Bir carinin fiyat tekliflerini listeleyen modal.
 * Sadece görüntüleme + silme (şimdilik faturaya çevirme yok).
 *
 * Props:
 *   contact: {id, name}
 *   onClose(): void
 *   onChanged?(): void  // silme sonrası özet yenilemek için opsiyonel
 */
export default function PriceQuotesModal({ contact, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // id -> bool
  const [company, setCompany] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | won | lost
  const [orderDialogFor, setOrderDialogFor] = useState(null); // quote or null
  const [orders, setOrders] = useState({}); // quote_id -> [SupplierOrder]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: qs }, { data: co }, { data: os }] = await Promise.all([
        api.get(`/price-quotes?contact_id=${contact.id}`),
        api.get("/settings/company").catch(() => ({ data: null })),
        api.get(`/supplier-orders?customer_contact_id=${contact.id}`).catch(() => ({ data: [] })),
      ]);
      setRows(qs || []);
      setCompany(co || null);
      // Group orders by quote_id
      const grouped = {};
      (os || []).forEach((o) => {
        if (o.quote_id) {
          (grouped[o.quote_id] = grouped[o.quote_id] || []).push(o);
        }
      });
      setOrders(grouped);
    } catch (e) {
      toast.error("Teklifler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [contact.id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (qid) => {
    if (!window.confirm("Teklif silinsin mi?")) return;
    try {
      await api.delete(`/price-quotes/${qid}`);
      toast.success("Silindi");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const setStatus = async (q, next, note) => {
    try {
      await api.patch(`/price-quotes/${q.id}/status`, { status: next, status_note: note || null });
      toast.success(next === "won" ? "Teklif Olumlu olarak işaretlendi"
                    : next === "lost" ? "Teklif Olumsuz olarak işaretlendi"
                    : "Teklif Beklemede'ye alındı");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Durum güncellenemedi: " + (e?.response?.data?.detail || e?.message));
    }
  };

  const openOrderDialog = (q) => setOrderDialogFor(q);
  const onOrderCreated = () => { setOrderDialogFor(null); load(); onChanged && onChanged(); };

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, won: 0, lost: 0 };
    rows.forEach((r) => { c[r.status || "pending"] = (c[r.status || "pending"] || 0) + 1; });
    return c;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((r) => (r.status || "pending") === statusFilter);
  }, [rows, statusFilter]);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const downloadPdf = async (q) => {
    try {
      await generateQuotePDF(q, contact, company);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error("PDF üretilemedi: " + (e?.message || ""));
    }
  };

  const shareWhatsApp = async (q) => {
    // Önce PDF üret + indir; ardından WhatsApp'ı özet metinle aç.
    // (wa.me PDF ekleme desteklemez; kullanıcı indirilen PDF'i manuel ekler)
    try {
      await generateQuotePDF(q, contact, company);
    } catch (e) {
      toast.error("PDF üretilemedi: " + (e?.message || ""));
      return;
    }
    const text = buildQuoteShareText(q, contact, company);
    const phone = String(contact?.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(phone ? "WhatsApp açıldı — PDF'i ekleyin" : "WhatsApp açıldı — kişi seçip PDF'i ekleyin");
  };

  const shareEmail = async (q) => {
    if (!contact?.email) { toast.error("Bu carinin e-postası kayıtlı değil"); return; }
    try {
      await generateQuotePDF(q, contact, company);
    } catch (e) {
      toast.error("PDF üretilemedi: " + (e?.message || ""));
      return;
    }
    const subject = `Fiyat Teklifi — ${fmtDate(q.date)}`;
    const body = buildQuoteShareText(q, contact, company);
    const url = `mailto:${encodeURIComponent(contact.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    toast.success("E-posta açıldı — PDF'i ekleyin");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="quotes-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="quotes-modal"
        className="bg-white border-2 border-black w-full max-w-4xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <div className="label-caps text-neutral-500">Fiyat Teklifleri</div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2 mt-1">
              <FileText size={16} /> {contact.name}
              <span className="text-xs text-neutral-500 font-normal">({rows.length} teklif)</span>
            </h2>
          </div>
          <button onClick={onClose} data-testid="quotes-close" className="p-1 text-xl leading-none">×</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-neutral-500">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 text-sm">
            Bu cari için henüz teklif yok. Fiyat Hesapla ekranından "Teklife Kaydet" ile ekleyebilirsiniz.
          </div>
        ) : (
          <>
            {/* Durum sekmeleri */}
            <div className="flex flex-wrap items-center gap-2 px-5 pt-4">
              {[
                { key: "all", label: "Tümü", cls: "bg-neutral-800" },
                { key: "pending", label: "Bekleyen", cls: "bg-neutral-500" },
                { key: "won", label: "Olumlu", cls: "bg-emerald-700" },
                { key: "lost", label: "Olumsuz", cls: "bg-rose-700" },
              ].map((t) => (
                <button
                  key={t.key}
                  data-testid={`quotes-tab-${t.key}`}
                  onClick={() => setStatusFilter(t.key)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest border-2 transition-colors ${
                    statusFilter === t.key
                      ? `${t.cls} text-white border-transparent`
                      : "border-black/20 text-neutral-700 hover:border-black"
                  }`}
                >
                  {t.label}
                  <span className={`ml-2 text-[10px] px-1 py-0.5 ${
                    statusFilter === t.key ? "bg-white/20" : "bg-neutral-100"
                  }`}>{counts[t.key] || 0}</span>
                </button>
              ))}
            </div>

            <div className="divide-y divide-black/10">
              {filteredRows.length === 0 ? (
                <div className="p-8 text-center text-neutral-500 text-sm">Bu sekmede teklif yok.</div>
              ) : filteredRows.map((q) => (
              <div key={q.id} className="p-4" data-testid={`quote-row-${q.id}`}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => toggle(q.id)}
                    data-testid={`quote-toggle-${q.id}`}
                    className="p-0.5 hover:bg-neutral-100 mt-0.5"
                    title="Detay"
                  >
                    {expanded[q.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                          <span>{fmtDate(q.date)}</span>
                          {(() => {
                            const primary = (q.title && q.title.trim())
                              || (q.stock_name && q.stock_name.trim())
                              || (q.items && q.items[0] && ((q.items[0].title && q.items[0].title.trim()) || (q.items[0].stock_name && q.items[0].stock_name.trim())));
                            const extra = (q.items && q.items.length > 1) ? ` +${q.items.length - 1}` : "";
                            return primary ? (
                              <span className="text-neutral-700 font-medium" data-testid={`quote-modal-product-${q.id}`}>
                                · {primary}{extra && <span className="text-neutral-400">{extra}</span>}
                              </span>
                            ) : null;
                          })()}
                          <StatusBadge status={q.status || "pending"} />
                        </div>
                        <div className="text-[10px] text-neutral-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {q.stock_sku && (
                            <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-500 text-emerald-800 px-1.5 py-0.5 font-mono-num text-[10px]"
                                  data-testid={`quote-stock-badge-${q.id}`}>
                              STK · {q.stock_sku}
                              {q.stock_name && <span className="normal-case font-normal ml-1 text-emerald-700">· {q.stock_name}</span>}
                            </span>
                          )}
                          {q.length_mm != null && (
                            <span className="font-mono-num">
                              {q.length_mm}×{q.width_mm}×{q.height_mm} mm × {q.quantity} adet
                            </span>
                          )}
                          {q.supplier_name && (
                            <span className="inline-flex items-center gap-1">
                              <Package size={11} /> {q.supplier_name} · {q.flute}/{q.quality}
                            </span>
                          )}
                          {q.shipping_total > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Truck size={11} /> {fmtTRY(q.shipping_total)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <div>
                          <div className="font-mono-num text-base font-bold text-emerald-700" data-testid={`quote-total-${q.id}`}>
                            {fmtTRY(q.grand_total)}
                          </div>
                          <div className="text-[10px] text-neutral-500">
                            Karton {fmtTRY(q.karton_total)} + Nakliye {fmtTRY(q.shipping_total)}
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 border-l border-black/10 pl-2 ml-1">
                          {/* Durum aksiyonları */}
                          {(q.status || "pending") === "pending" ? (
                            <>
                              <button
                                onClick={() => setStatus(q, "won")}
                                data-testid={`quote-set-won-${q.id}`}
                                className="p-1.5 hover:bg-emerald-50 text-emerald-700"
                                title="Olumlu (kabul)"
                              >
                                <ThumbsUp size={13} />
                              </button>
                              <button
                                onClick={() => setStatus(q, "lost")}
                                data-testid={`quote-set-lost-${q.id}`}
                                className="p-1.5 hover:bg-rose-50 text-rose-600"
                                title="Olumsuz (red)"
                              >
                                <ThumbsDown size={13} />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setStatus(q, "pending")}
                              data-testid={`quote-reset-${q.id}`}
                              className="p-1.5 hover:bg-neutral-100 text-neutral-600"
                              title="Beklemeye al"
                            >
                              <RotateCcw size={13} />
                            </button>
                          )}
                          {(q.status || "pending") === "won" && (
                            <button
                              onClick={() => openOrderDialog(q)}
                              data-testid={`quote-open-order-${q.id}`}
                              className="p-1.5 hover:bg-amber-50 text-amber-700"
                              title="Tedarikçiye Sipariş Aç"
                            >
                              <ShoppingCart size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => downloadPdf(q)}
                            data-testid={`quote-pdf-${q.id}`}
                            className="p-1.5 hover:bg-neutral-100 text-neutral-700"
                            title="PDF İndir"
                          >
                            <Download size={13} />
                          </button>
                          <button
                            onClick={() => shareWhatsApp(q)}
                            data-testid={`quote-whatsapp-${q.id}`}
                            className="p-1.5 hover:bg-emerald-50 text-emerald-700"
                            title={contact?.phone ? `WhatsApp'ta gönder (${contact.phone})` : "WhatsApp'ta paylaş (kişi seçin)"}
                          >
                            <Send size={13} />
                          </button>
                          <button
                            onClick={() => shareEmail(q)}
                            data-testid={`quote-email-${q.id}`}
                            disabled={!contact?.email}
                            className="p-1.5 hover:bg-sky-50 text-sky-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={contact?.email ? `E-posta gönder (${contact.email})` : "Carinin e-postası kayıtlı değil"}
                          >
                            <Mail size={13} />
                          </button>
                          <button
                            onClick={() => remove(q.id)}
                            data-testid={`quote-remove-${q.id}`}
                            className="p-1.5 hover:bg-rose-50 text-rose-600"
                            title="Sil"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {expanded[q.id] && <ExpandedDetail q={q} orders={orders[q.id] || []} />}
                  </div>
                </div>
              </div>
            ))}
            </div>
          </>
        )}
      </div>
      {orderDialogFor && (
        <SupplierOrderDialog
          quote={orderDialogFor}
          onClose={() => setOrderDialogFor(null)}
          onCreated={onOrderCreated}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "won") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-700 text-white px-1.5 py-0.5">
        <ThumbsUp size={9} /> Olumlu
      </span>
    );
  }
  if (status === "lost") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-rose-700 text-white px-1.5 py-0.5">
        <ThumbsDown size={9} /> Olumsuz
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-neutral-500 text-white px-1.5 py-0.5">
      Bekliyor
    </span>
  );
}

function ExpandedDetail({ q, orders }) {
  return (
    <div className="mt-3 border border-black/10 p-3 bg-neutral-50 space-y-3 text-xs">
      {orders && orders.length > 0 && (
        <div>
          <div className="label-caps mb-1.5 flex items-center gap-1.5">
            <ShoppingCart size={11} /> Tedarikçi Siparişleri ({orders.length})
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-black/10 text-neutral-500">
                <th className="text-left py-1">Sipariş No</th>
                <th className="text-left py-1">Tedarikçi</th>
                <th className="text-left py-1">Tarih</th>
                <th className="text-right py-1">Adet</th>
                <th className="text-right py-1">Tutar</th>
                <th className="text-left py-1 pl-2">Durum</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-black/5" data-testid={`order-row-${o.id}`}>
                  <td className="py-1 font-mono-num font-semibold">{o.order_no}</td>
                  <td className="py-1"><SupplierName id={o.supplier_contact_id} /></td>
                  <td className="py-1 font-mono-num">{o.date}</td>
                  <td className="py-1 text-right font-mono-num">{o.quantity}</td>
                  <td className="py-1 text-right font-mono-num">{fmtTRY(o.total_cost)}</td>
                  <td className="py-1 pl-2">
                    <OrderStatusBadge status={o.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Info label="Adet başı m²" value={q.sheet_m2 != null ? Number(q.sheet_m2).toLocaleString("tr-TR", { maximumFractionDigits: 6 }) : "—"} />
        <Info label="Toplam m²" value={q.total_m2 != null ? Number(q.total_m2).toLocaleString("tr-TR", { maximumFractionDigits: 4 }) : "—"} />
        <Info label="Karton Toplam" value={fmtTRY(q.karton_total)} />
        <Info label="Nakliye Toplam" value={fmtTRY(q.shipping_total)} />
        <Info label="Kutu Başı Nakliye" value={fmtTRY(q.shipping_per_unit)} />
        <Info label="Kutu Başı Satış" value={q.quantity > 0 ? fmtTRY(q.grand_total / q.quantity) : "—"} />
        <div className="col-span-2">
          <Info label="Genel Toplam" value={fmtTRY(q.grand_total)} strong />
        </div>
      </div>

      {q.shipping_lines && q.shipping_lines.length > 0 && (
        <div>
          <div className="label-caps mb-1.5 flex items-center gap-1.5"><Truck size={11} /> Nakliye Satırları</div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-black/10 text-neutral-500">
                <th className="text-left py-1">Güzergâh</th>
                <th className="text-left py-1">Araç</th>
                <th className="text-right py-1">Mesafe</th>
                <th className="text-right py-1">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {q.shipping_lines.map((s, i) => (
                <tr key={i} className="border-b border-black/5">
                  <td className="py-1">{s.from_location} → {s.to_location}</td>
                  <td className="py-1">{s.vehicle_name || "—"}</td>
                  <td className="py-1 text-right font-mono-num">{s.distance_km} km{s.return_trip ? " ×2" : ""}</td>
                  <td className="py-1 text-right font-mono-num">{fmtTRY(s.total_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {q.comparisons && q.comparisons.length > 0 && (
        <div>
          <div className="label-caps mb-1.5 flex items-center gap-1.5"><Package size={11} /> Karşılaştırma Snapshot</div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-black/10 text-neutral-500">
                <th className="text-left py-1">Tedarikçi</th>
                <th className="text-left py-1">Karton</th>
                <th className="text-right py-1">Karton TL</th>
                <th className="text-right py-1">Nakliye TL</th>
                <th className="text-right py-1">Genel</th>
              </tr>
            </thead>
            <tbody>
              {q.comparisons.map((c) => (
                <tr key={c.key} className="border-b border-black/5">
                  <td className="py-1">{c.supplier}</td>
                  <td className="py-1">{c.flute} · {c.quality}</td>
                  <td className="py-1 text-right font-mono-num">{fmtTRY(c.total_cost)}</td>
                  <td className="py-1 text-right font-mono-num">{c.shipping_total > 0 ? fmtTRY(c.shipping_total) : "—"}</td>
                  <td className="py-1 text-right font-mono-num font-semibold">{fmtTRY(c.grand_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {q.notes && (
        <div>
          <div className="label-caps mb-1">Not</div>
          <div className="text-neutral-700">{q.notes}</div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, strong }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`mt-0.5 ${strong ? "font-mono-num text-base font-bold text-emerald-700" : "font-mono-num"}`}>{value}</div>
    </div>
  );
}

function OrderStatusBadge({ status }) {
  const map = {
    open:      { bg: "bg-amber-100 text-amber-800 border-amber-300",     label: "Açık" },
    received:  { bg: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Teslim Alındı" },
    cancelled: { bg: "bg-neutral-200 text-neutral-600 border-neutral-300", label: "İptal" },
  };
  const s = map[status] || map.open;
  return (
    <span className={`inline-flex text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${s.bg}`}>{s.label}</span>
  );
}

/** Küçük yardımcı — cari isimlerini cache'leyerek göstermek yerine tek çağrı yapar */
function SupplierName({ id }) {
  const [name, setName] = useState("—");
  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    api.get(`/contacts/${id}`).then((r) => { if (!cancelled) setName(r.data?.name || "—"); }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);
  return <span>{name}</span>;
}
